import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "../../hooks/useTranslation";
import SpeakerBadge from "./SpeakerBadge";
import {
  countSentencesForSpeaker,
  getDisplayName,
  getDisplayRole,
} from "../../utils/speakerHelpers";

/**
 * Dialog para "mesclar" dois ou mais speakers em um único.
 *
 * Fluxo escolhido pelo produto: ao invés de "excluir um speaker e remapear
 * suas frases", o usuário marca dois (ou mais) speakers que são, na verdade,
 * a mesma pessoa. Em seguida escolhe qual deles é o "alvo" (cujo
 * `speaker_id` sobrevive) e pode opcionalmente ajustar o nome/papel final.
 *
 * Props:
 *   - `open`         boolean
 *   - `transcription`{speakers, sentences} — usado só para leitura (nomes,
 *                    contagens). Quem altera o estado é o consumidor.
 *   - `initialSelectedIds` array opcional para já vir com 1+ speakers marcados.
 *   - `onClose`      () => void
 *   - `onConfirm`    ({ mergeIds, targetId, name, role }) => void
 */
function SpeakersMergeDialog({
  open,
  transcription,
  initialSelectedIds = [],
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation("common");
  /* Memoizamos para que mudar `transcription` não recrie a referência a
     cada render — caso contrário, os hooks abaixo entrariam em loop. */
  const speakers = useMemo(
    () => transcription?.speakers || [],
    [transcription],
  );
  const sentences = useMemo(
    () => transcription?.sentences || [],
    [transcription],
  );
  const unidentifiedLabel = t("transcription.speakers.unidentified");

  const [selectedIds, setSelectedIds] = useState(() =>
    initialSelectedIds.filter((id) =>
      speakers.some((s) => s.speaker_id === id),
    ),
  );
  const [targetId, setTargetId] = useState(initialSelectedIds[0] || null);
  const [editedName, setEditedName] = useState("");
  const [editedRole, setEditedRole] = useState("");

  /* Quando o diálogo (re)abre, semeamos com a seleção sugerida e escolhemos
     como alvo o primeiro speaker selecionado que tenha um nome legítimo;
     se nenhum tiver, mantemos o primeiro selecionado mesmo assim.
     IMPORTANTE: este efeito deve rodar APENAS na transição
     `open: false → true`. Antes dependia diretamente de
     `initialSelectedIds` (que tem default `= []` no destructuring → novo
     array a cada render), o que fazia o efeito disparar a cada clique
     no row e reescrever `selectedIds` para vazio — dando a impressão de
     que os checkboxes "não eram clicáveis". Mantemos as props mais
     recentes em refs e detectamos a transição com `wasOpenRef`. */
  const initialSelectedIdsRef = useRef(initialSelectedIds);
  const speakersRef = useRef(speakers);
  useEffect(() => {
    initialSelectedIdsRef.current = initialSelectedIds;
  }, [initialSelectedIds]);
  useEffect(() => {
    speakersRef.current = speakers;
  }, [speakers]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const seed = initialSelectedIdsRef.current || [];
      const list = speakersRef.current || [];
      const valid = seed.filter((id) =>
        list.some((s) => s.speaker_id === id),
      );
      setSelectedIds(valid);

      const namedTarget = valid.find((id) => {
        const sp = list.find((s) => s.speaker_id === id);
        return sp && getDisplayName(sp, "") !== "";
      });
      setTargetId(namedTarget || valid[0] || null);
    }
    wasOpenRef.current = open;
  }, [open]);

  /* Sempre que o alvo muda, propomos seu nome/papel como sugestão inicial
     para o resultado da mesclagem (o usuário pode reajustar). */
  useEffect(() => {
    const target = speakers.find((s) => s.speaker_id === targetId);
    setEditedName(getDisplayName(target, "") || target?.name || "");
    setEditedRole(getDisplayRole(target) || "");
  }, [targetId, speakers]);

  const totalAffected = useMemo(
    () =>
      selectedIds.reduce(
        (acc, id) => acc + countSentencesForSpeaker(id, sentences),
        0,
      ),
    [selectedIds, sentences],
  );

  /* Liga/desliga um speaker da seleção. Regras:
     - Ao MARCAR: se ainda não havia targetId definido (ou o atual não
       está mais selecionado), promovemos o speaker recém-marcado a
       target — assim o radio acompanha o checkbox naturalmente.
     - Ao DESMARCAR: se este era o target, escolhemos o próximo target
       como o primeiro speaker que continuar selecionado (ou null, se
       não sobrar nenhum). */
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((p) => p !== id);
        if (id === targetId) {
          setTargetId(next[0] || null);
        }
        return next;
      }
      const next = [...prev, id];
      if (!targetId || !prev.includes(targetId)) {
        /* Não havia target válido — assume o primeiro escolhido como
           "speaker que sobrevive". O usuário pode mudar via radio. */
        setTargetId(id);
      }
      return next;
    });
  };

  /* Define um speaker como o "alvo" da mesclagem. Se ele ainda não
     estava selecionado, marcamos o checkbox dele automaticamente —
     evita que o usuário precise dar dois cliques (1 no check + 1 no
     radio) para escolher um novo alvo. */
  const setTargetAndEnsureSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setTargetId(id);
  };

  const canConfirm =
    selectedIds.length >= 2 && targetId && selectedIds.includes(targetId);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.({
      mergeIds: selectedIds,
      targetId,
      name: editedName.trim(),
      role: editedRole.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="transcription-speaker-merge-dialog"
    >
      <DialogTitle>{t("transcription.speakers.dialogs.mergeTitle")}</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          className="transcription-speaker-merge-dialog__intro"
        >
          {t("transcription.speakers.dialogs.mergeIntro")}
        </Typography>

        <Box className="transcription-speaker-merge-dialog__list">
          {speakers.map((sp) => {
            const checked = selectedIds.includes(sp.speaker_id);
            const isTarget = sp.speaker_id === targetId;
            const count = countSentencesForSpeaker(sp.speaker_id, sentences);
            /* A linha INTEIRA é clicável: clicar em qualquer lugar (que
               não seja o radio) liga/desliga o checkbox. Isso elimina a
               sensação de "não aceita cliques" que acontecia quando o
               usuário clicava sobre o badge (entre o check e o radio).
               O radio fica em sua própria área clicável (com handler de
               stopPropagation) para escolher independentemente o "alvo". */
            return (
              <Box
                key={sp.speaker_id}
                role="button"
                tabIndex={0}
                onClick={() => toggleSelected(sp.speaker_id)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    toggleSelected(sp.speaker_id);
                  }
                }}
                className={[
                  "transcription-speaker-merge-dialog__row",
                  checked ? "is-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Box className="transcription-speaker-merge-dialog__check">
                  <Checkbox
                    color="primary"
                    checked={checked}
                    /* Sem `onChange`/`onClick` próprios: o toggle vem do
                       click no row inteiro (acima). Mantemos o checkbox
                       apenas como indicador visual, com `tabIndex={-1}`
                       para não duplicar o foco do row. */
                    tabIndex={-1}
                    inputProps={{ "aria-label": "select-speaker" }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelected(sp.speaker_id)}
                  />
                  <SpeakerBadge
                    speaker={sp}
                    speakers={speakers}
                    variant="inline"
                  />
                </Box>
                <Typography
                  variant="caption"
                  className="transcription-speaker-merge-dialog__count"
                >
                  {t("transcription.speakers.dialogs.sentenceCount", {
                    count,
                  })}
                </Typography>
                <FormControlLabel
                  className="transcription-speaker-merge-dialog__target"
                  /* Para impedir que clicar no radio também alternasse o
                     checkbox via bubbling do click do row. */
                  onClick={(e) => e.stopPropagation()}
                  control={
                    <Radio
                      color="primary"
                      checked={isTarget}
                      onChange={() => setTargetAndEnsureSelected(sp.speaker_id)}
                    />
                  }
                  label={t("transcription.speakers.dialogs.keepThis")}
                />
              </Box>
            );
          })}
        </Box>

        {selectedIds.length >= 2 && (
          <>
            <Divider className="transcription-speaker-merge-dialog__divider" />
            <Typography
              variant="body2"
              className="transcription-speaker-merge-dialog__summary"
            >
              {t("transcription.speakers.dialogs.mergeSummary", {
                affected: totalAffected,
                kept:
                  getDisplayName(
                    speakers.find((s) => s.speaker_id === targetId),
                    unidentifiedLabel,
                  ) || unidentifiedLabel,
              })}
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label={t("transcription.speakers.fieldName")}
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label={t("transcription.speakers.fieldRole")}
              value={editedRole}
              onChange={(e) => setEditedRole(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {t("transcription.speakers.dialogs.mergeConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SpeakersMergeDialog;
